import { useState, useEffect } from 'react';
import api from '../api/axios';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';

const DEFAULTS = {
  badge: 'Corporate',
  title: 'Custom pricing',
  description: 'For companies with large-scale recruitment needs.',
  button_label: 'Talk to Sales',
  featuresText: [
    'Bulk job posting',
    'Multiple HR users',
    'Multi-location recruitment',
    'Centralised recruitment dashboard',
    'Custom analytics',
    'API integrations, subject to availability',
    'Dedicated account management',
  ].join('\n'),
  is_active: true,
};

export default function EnterpriseCardPanel() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/admin/enterprise/card')
      .then(({ data }) => {
        if (data) {
          setForm({
            badge: data.badge ?? DEFAULTS.badge,
            title: data.title ?? DEFAULTS.title,
            description: data.description ?? '',
            button_label: data.button_label ?? DEFAULTS.button_label,
            featuresText: Array.isArray(data.features) ? data.features.join('\n') : '',
            is_active: data.is_active !== false,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      await api.put('/admin/enterprise/card', {
        badge: form.badge.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        button_label: form.button_label.trim(),
        features: form.featuresText.split('\n').map((s) => s.trim()).filter(Boolean),
        is_active: !!form.is_active,
      });
      setMsg('Corporate card saved.');
    } catch (e) {
      const errs = e.response?.data?.errors;
      setErr(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-4">
      <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Corporate / Enterprise card</h3>
      <p className="text-sm text-gray-500 mb-4">
        The dark "Custom pricing" card on the employer billing page. The button opens the Enterprise Enquiry form.
      </p>

      {err && <p className="text-sm text-red-600 mb-2">{err}</p>}
      {msg && <p className="text-sm text-green-600 mb-2">{msg}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <label className="text-xs text-gray-500">Badge text
          <input className={`${inputCls} mt-1`} value={form.badge} onChange={(e) => set('badge', e.target.value)} />
        </label>
        <label className="text-xs text-gray-500">Heading
          <input className={`${inputCls} mt-1`} value={form.title} onChange={(e) => set('title', e.target.value)} />
        </label>
        <label className="text-xs text-gray-500">Button label
          <input className={`${inputCls} mt-1`} value={form.button_label} onChange={(e) => set('button_label', e.target.value)} />
        </label>
        <label className="text-xs text-gray-500 sm:col-span-3">Description
          <input className={`${inputCls} mt-1`} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </label>
      </div>

      <label className="block text-xs text-gray-500 mb-3">
        Features (one per line)
        <textarea rows={8} className={`${inputCls} mt-1 font-mono`} value={form.featuresText}
          onChange={(e) => set('featuresText', e.target.value)} />
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
        Show this card to employers
      </label>

      <button type="button" onClick={save} disabled={saving || !form.title.trim()}
        className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  );
}